import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import apiClient from "../api/apiClient";

const emptySettings = {
  enabled: false,
  mode: "cti_bridge",
  odooBaseUrl: "",
  odooDatabase: "",
  providerName: "Mayday PBX",
  pbxDomain: "",
  websocketUrl: "",
  apiBaseUrl: "",
  clickToCallPath: "/api/integrations/odoo/click-to-call",
  clickToCallUrl: "",
  webhookSecret: "",
  defaultContext: "from-internal",
  defaultPriority: 1,
};

const ODOO_MODE_OPTIONS = [
  {
    value: "cti_bridge",
    label: "Click-to-Call via Mayday",
    description: "Odoo starts calls through Mayday; Asterisk rings the agent and handles the dialplan.",
  },
  {
    value: "native_softphone",
    label: "Odoo Browser Softphone",
    description: "Odoo registers SIP/WebRTC directly and the browser carries the audio.",
  },
  {
    value: "both",
    label: "Hybrid: Odoo + Mayday",
    description: "Enable both Odoo browser calling and Mayday-managed click-to-call.",
  },
];

const getOdooModeLabel = (mode) =>
  ODOO_MODE_OPTIONS.find((option) => option.value === mode)?.label || mode || "";

const getOdooModeDescription = (mode) =>
  ODOO_MODE_OPTIONS.find((option) => option.value === mode)?.description || "";

const Integrations = () => {
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/integrations/odoo/settings");
      setSettings({ ...emptySettings, ...response.data.data });
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to load Odoo settings",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveSettings = async (rotateSecret = false) => {
    setSaving(true);
    try {
      const response = await apiClient.put("/integrations/odoo/settings", {
        ...settings,
        rotateSecret,
      });
      setSettings({ ...emptySettings, ...response.data.data });
      enqueueSnackbar(response.data.message || "Odoo settings saved", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to save Odoo settings",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const copyConnectionSummary = async () => {
    const text = [
      "Mayday Odoo Integration",
      `Mode: ${getOdooModeLabel(settings.mode)}`,
      `Provider Name: ${settings.providerName}`,
      `WebSocket: ${settings.websocketUrl}`,
      `PBX Server IP: ${settings.pbxDomain}`,
      `Click-to-call URL: ${settings.clickToCallUrl}`,
      `Webhook Header: X-Odoo-Webhook-Secret`,
      `Webhook Secret: ${settings.webhookSecret}`,
    ].join("\n");

    await navigator.clipboard.writeText(text);
    enqueueSnackbar("Odoo integration parameters copied", {
      variant: "success",
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Odoo Integration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mayday provisions Odoo calling while Asterisk remains the telephony authority.
          </Typography>
        </Box>
        <Chip
          color={settings.enabled ? "success" : "default"}
          label={settings.enabled ? "Enabled" : "Disabled"}
        />
      </Stack>

      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <Typography>Loading settings...</Typography>
          ) : (
            <Stack spacing={3}>
              <Alert severity="info">
                Use native softphone mode for Odoo browser audio, CTI bridge mode for Odoo-triggered calls that ring Mayday/Electron or a desk phone.
              </Alert>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings.enabled)}
                    onChange={handleChange}
                    name="enabled"
                  />
                }
                label="Enable Odoo integration"
              />

              <FormControl fullWidth>
                <InputLabel id="odoo-mode-label">Integration Mode</InputLabel>
                <Select
                  labelId="odoo-mode-label"
                  name="mode"
                  value={settings.mode}
                  label="Integration Mode"
                  onChange={handleChange}
                >
                  {ODOO_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {getOdooModeDescription(settings.mode)}
                </Typography>
              </FormControl>

              <Divider />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Odoo Base URL"
                  name="odooBaseUrl"
                  value={settings.odooBaseUrl || ""}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="Odoo Database"
                  name="odooDatabase"
                  value={settings.odooDatabase || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Provider Name"
                  name="providerName"
                  value={settings.providerName || ""}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="PBX Server IP / Domain"
                  name="pbxDomain"
                  value={settings.pbxDomain || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>

              <TextField
                label="SIP WebSocket URL"
                name="websocketUrl"
                value={settings.websocketUrl || ""}
                onChange={handleChange}
                fullWidth
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Mayday API Base URL"
                  name="apiBaseUrl"
                  value={settings.apiBaseUrl || ""}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="Click-to-call Path"
                  name="clickToCallPath"
                  value={settings.clickToCallPath || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Outbound Context"
                  name="defaultContext"
                  value={settings.defaultContext || ""}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="Priority"
                  name="defaultPriority"
                  type="number"
                  value={settings.defaultPriority || 1}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Click-to-call URL"
                value={settings.clickToCallUrl || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />

              <TextField
                label="Webhook Secret"
                value={settings.webhookSecret || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  startIcon={<RefreshIcon />}
                  disabled={saving}
                  onClick={() => saveSettings(true)}
                >
                  Rotate Secret
                </Button>
                <Button
                  startIcon={<ContentCopyIcon />}
                  onClick={copyConnectionSummary}
                >
                  Copy Parameters
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  onClick={() => saveSettings(false)}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Integrations;
